/**
 * Réglages → **Activité (intervals.icu)**
 * (`ARCHITECTURE_V2.md` §5.2, §5.3).
 *
 * Ce volet répond à deux questions, et à elles seules : **est-ce que ça marche,
 * et sinon pourquoi** — puis **comment y remédier sans ouvrir un terminal**.
 *
 * 🔴 **Le champ de clé est un revirement assumé** *(05/09/2026, demandé après
 * usage)*. Le cahier des charges de l'étape disait « jamais un champ de saisie
 * de clé dans l'interface, la clé reste un secret serveur ». L'argument était
 * défendable et il ratait le coût du geste : poser ou changer la clé demandait
 * un `scp`, un `ssh -t`, un mot de passe sudo tapé dans un terminal externe, et
 * un script. Supportable pour un réglage qu'on touche deux fois dans la vie de
 * l'application ; plus du tout à la première clé refusée.
 *
 * ⚠️ **Ce que ce revirement coûte, écrit une fois pour qu'on ne le redécouvre
 * pas** : la clé est stockée en base, donc présente **en clair dans chaque
 * sauvegarde** (§8.2). C'est l'inverse de `session.id`, qui ne garde qu'un
 * SHA-256 pour cette raison exacte — mais une clé d'API doit être **réémise** à
 * chaque requête, donc elle ne peut pas être hachée. Les sauvegardes vivent en
 * `750 nutri:nutri` sur le même VPS, au même niveau de protection que la base.
 *
 * ⚠️ **Le champ est en ÉCRITURE SEULE.** Il s'ouvre vide, à chaque fois, et la
 * clé posée n'en ressort jamais : le serveur ne la renvoie pas, pas même
 * tronquée. On ne reconnaît pas une clé d'API à ses quatre derniers caractères,
 * donc un aperçu serait une fuite pour un confort nul.
 *
 * ⚠️ **Distinct du volet « Synchronisation »**, qui le précède dans le sommaire.
 * Les deux portent le même mot et n'ont rien à voir : l'autre est la file des
 * saisies faites hors ligne (§3.3), celui-ci l'ingestion **entrante** d'un
 * tiers. Les fondre ferait chercher longtemps pourquoi « 3 en attente » ne parle
 * pas de vélo.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import type { IntervalsResponse, IntervalsRunDTO } from '../../../../shared/types';
import { readError } from '../../api/client';
import { fetchIntervals, INTERVALS_KEY, saveIntervalsKey, syncIntervals } from '../../api/intervals';
import { Button, Card, TextField } from '../../components/nourri';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { useConfirm } from '../../hooks/useConfirm';
import { formatCount, formatSyncedAt } from '../progress/activityFields';

/** Une ligne « libellé / valeur », composée de classes existantes. */
function Ligne({ label, value }: { label: string; value: string }) {
  return (
    <div className="n-row n-row--between">
      <span style={{ fontSize: 13, color: 'var(--ink-soft)', fontWeight: 600 }}>{label}</span>
      <strong className="tnum" style={{ fontSize: 14 }}>
        {value}
      </strong>
    </div>
  );
}

/** D'où vient la clé en vigueur, en une phrase lisible. */
function sourceLabel(source: 'setting' | 'env' | null): string {
  switch (source) {
    case 'setting':
      return 'saisie ici';
    case 'env':
      return 'posée sur le serveur';
    case null:
      return 'aucune';
  }
}

/**
 * Le verdict de la dernière ingestion déclenchée **à la main**.
 *
 * ⚠️ Distinct de `status.lastSyncOk`, qui décrit la dernière ingestion *quelle
 * qu'elle soit* — y compris un tick de fond d'il y a vingt minutes. Sans ce
 * retour immédiat, presser « Synchroniser » un jour où rien n'a changé ne
 * produirait aucun signe visible, et on presserait encore.
 */
function RunNote({ run }: { run: IntervalsRunDTO }) {
  if (run.status === 'skipped') {
    return <Hint>Aucune clé : rien n’a été demandé à intervals.icu.</Hint>;
  }
  if (run.status === 'error') {
    return <ErrorNote>Échec : {run.error ?? 'motif inconnu'}</ErrorNote>;
  }
  return (
    <Hint>
      Importé : {formatCount(run.activities)} activité(s) et {formatCount(run.wellness)} jour(s)
      de santé.
    </Hint>
  );
}

export function IntervalsSection() {
  const queryClient = useQueryClient();
  const intervals = useQuery({ queryKey: INTERVALS_KEY, queryFn: fetchIntervals });

  const [cle, setCle] = useState('');
  const [dernierRun, setDernierRun] = useState<IntervalsRunDTO | null>(null);

  /*
   * ⚠️ Confirmation **à la réponse**, pas au geste : rien n'est optimiste ici.
   * Poser une clé ou lancer une ingestion dépend entièrement du serveur, et
   * confirmer avant sa réponse annoncerait un succès qui peut encore échouer.
   * Même arbitrage que la pesée (étape 8 de la V1).
   */
  const { confirming, confirm } = useConfirm();

  /**
   * Toute mutation renvoie l'état complet : on le pose directement dans le
   * cache plutôt que d'invalider et de refaire un aller-retour. C'est la
   * convention des mutations de journal et de poids.
   */
  const appliquer = (response: IntervalsResponse) => {
    queryClient.setQueryData(INTERVALS_KEY, response);
    setDernierRun(response.lastRun ?? null);
    confirm();
  };

  const synchroniser = useMutation({
    mutationFn: syncIntervals,
    onSuccess: appliquer,
  });

  const poserCle = useMutation({
    mutationFn: (valeur: string | null) => saveIntervalsKey(valeur),
    onSuccess: (response) => {
      appliquer(response);
      // ⚠️ Le champ se vide **au succès seulement** : sur un échec, la clé
      // tapée reste sous les yeux pour être corrigée plutôt que retapée.
      setCle('');
    },
  });

  const lecture = readError(intervals);
  const status = intervals.data?.status;
  const occupe = synchroniser.isPending || poserCle.isPending;
  const erreurMutation = synchroniser.error ?? poserCle.error ?? null;

  return (
    <>
      {/* 🔴 L'erreur de lecture **après** les données (V2 §3.5) : hors ligne,
          le statut déjà en cache reste la bonne réponse à « est-ce configuré ? ». */}
      <ReadNote error={lecture} quoi="l’état de la synchronisation" />
      {status === undefined && lecture === null && <Hint>Chargement…</Hint>}

      {status && (
        <>
          {erreurMutation && <ErrorNote>{erreurMutation.message}</ErrorNote>}

          <Card>
            <div className="n-stack">
              <Ligne label="État" value={status.configured ? 'Configurée' : 'Non configurée'} />
              <Ligne label="Clé" value={sourceLabel(status.keySource)} />
              {status.configured && (
                <>
                  <Ligne
                    label="Athlète"
                    value={status.athleteId === '0' ? 'authentifié' : status.athleteId}
                  />
                  <Ligne label="Fenêtre" value={`${formatCount(status.windowDays)} jours`} />
                  <Ligne
                    label="Dernière synchronisation"
                    value={formatSyncedAt(status.lastSyncAt)}
                  />
                  {status.lastSyncOk === true && (
                    <Ligne
                      label="Dernier import"
                      value={`${formatCount(status.lastActivityCount)} act. · ${formatCount(
                        status.lastWellnessCount,
                      )} j. santé`}
                    />
                  )}
                </>
              )}

              {/*
                🔴 **Le bouton de synchronisation.** L'ingestion tourne toutes
                les 30 minutes ; ce bouton existe pour les deux moments où
                l'attendre n'a pas de sens — juste après avoir posé une clé, et
                au retour d'une sortie qu'on veut voir tout de suite.

                ⚠️ Désactivé sans clé : il n'y aurait rien à demander, et un
                bouton qui répond « rien fait » apprend à ne plus s'en servir.
              */}
              <Button
                variant={confirming ? 'selected' : 'secondary'}
                disabled={occupe || !status.configured}
                onClick={() => {
                  synchroniser.mutate();
                }}
              >
                {synchroniser.isPending ? 'Synchronisation…' : 'Synchroniser maintenant'}
              </Button>

              {dernierRun !== null && <RunNote run={dernierRun} />}
            </div>
          </Card>

          {/*
            ⚠️ Un échec est **nommé**, jamais tu — la discipline de la file
            (§3.3 : « une opération en échec n'est jamais supprimée en silence,
            elle reste visible avec son motif »). Et il dit ce qui se passe
            ensuite : sans cette phrase, on croit qu'il faut agir.
          */}
          {status.configured && status.lastSyncOk === false && status.lastError !== null && (
            <Card>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Dernière tentative en échec</p>
              <p className="n-hint" style={{ marginTop: 6 }}>
                {status.lastError}
              </p>
              <p className="n-hint" style={{ marginTop: 6 }}>
                Une nouvelle tentative a lieu automatiquement dans moins de 30 minutes.
                Rien d’autre n’est affecté : la synchronisation est une fonction
                indicative, elle ne conditionne aucun autre écran.
              </p>
            </Card>
          )}

          {/* ==========================================================
              La clé
              ========================================================== */}
          <Card>
            <div className="n-stack">
              <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Clé d’API</p>

              <p className="n-hint" style={{ margin: 0 }}>
                Sur intervals.icu : <strong>Settings → Developer Settings → API Key</strong>.
              </p>

              {/*
                ⚠️ **En écriture seule, et le champ le dit.** Il s'ouvre vide à
                chaque fois : la clé posée n'est jamais renvoyée par le serveur,
                donc il n'y a rien à y afficher. `type="password"` n'apporterait
                qu'un masquage de saisie sur un champ qui ne contient jamais
                rien d'autre que ce qu'on vient de taper — et empêcherait de
                relire ce qu'on a collé, ce qui est précisément le geste ici.
              */}
              <TextField
                label={status.keySource === 'setting' ? 'Remplacer la clé' : 'Nouvelle clé'}
                value={cle}
                onChange={(event) => {
                  setCle(event.target.value);
                }}
                placeholder="Collez la clé ici"
                autoComplete="off"
                spellCheck={false}
                /*
                 * ⚠️ **`selectOnFocus` désactivé, seule exception de l'écran.**
                 * Le comportement par défaut sélectionne tout à la prise de
                 * focus, parce que « les champs de ce projet sont presque
                 * toujours pré-remplis » et qu'on les *corrige*. Ici le champ
                 * est vide par construction et on y **colle** : sélectionner
                 * n'aurait rien à sélectionner, et rouvrir le champ après une
                 * saisie effacerait ce qu'on vient de coller au premier
                 * caractère tapé.
                 */
                selectOnFocus={false}
              />

              <Button
                disabled={occupe || cle.trim().length === 0}
                onClick={() => {
                  poserCle.mutate(cle.trim());
                }}
              >
                {poserCle.isPending ? 'Enregistrement…' : 'Enregistrer et synchroniser'}
              </Button>

              {/*
                🔴 **Enregistrer déclenche une ingestion, et c'est tout
                l'intérêt.** La seule question qu'on se pose en tapant une clé
                est « est-elle bonne ? », et aucune validation de forme ne sait y
                répondre — intervals.icu ne publie aucun format garanti. L'appel,
                lui, répond en une seconde.
              */}
              <p className="n-hint" style={{ margin: 0 }}>
                La clé est vérifiée en lançant une synchronisation aussitôt : le résultat
                s’affiche ci-dessus.
              </p>

              {status.keySource === 'setting' && (
                <>
                  <Button
                    variant="secondary"
                    disabled={occupe}
                    onClick={() => {
                      poserCle.mutate(null);
                    }}
                  >
                    Retirer la clé saisie ici
                  </Button>
                  {/*
                    ⚠️ **Dire ce qui se passe après le retrait.** Le serveur ne
                    peut pas écrire dans `/etc/nutri/nutri.env`
                    (`ProtectSystem=strict`) : retirer la clé de l'application
                    fait donc **retomber sur celle du serveur** si elle existe,
                    plutôt que de désactiver la synchronisation. Le taire
                    rendrait le geste incompréhensible.
                  */}
                  <p className="n-hint" style={{ margin: 0 }}>
                    La synchronisation reprendra la clé posée sur le serveur s’il y en a
                    une, sinon elle s’arrêtera.
                  </p>
                </>
              )}

              {/*
                🔴 **Le coût du stockage, dit une fois, à l'endroit où l'on
                décide.** Une clé saisie ici vit en base, donc dans les
                sauvegardes. C'est acceptable sur un VPS mono-utilisateur — il
                faut juste que ce soit su, pas découvert.
              */}
              <p className="n-hint" style={{ margin: 0 }}>
                Une clé saisie ici est conservée par le serveur et se retrouve dans ses
                sauvegardes. Pour la garder hors de la base, la poser plutôt par{' '}
                <code>infra/set-intervals-env.sh</code>.
              </p>
            </div>
          </Card>

          {/*
            🔴 **Le rappel de neutralité, à l'endroit où l'on se demande à quoi
            ça sert.** Le §7.3 des specs est catégorique et le §5.3 le tient par
            construction ; l'écrire ici évite qu'on cherche un jour le réglage
            « ajuster mes macros selon mes dépenses », qui n'existe pas et
            n'existera pas.
          */}
          <p className="n-foot-note">
            Les données importées sont <strong>indicatives</strong>. Elles ne modifient
            aucun objectif de macros, et le poids relevé par la montre n’est jamais
            enregistré comme pesée.
          </p>

          <p className="n-foot-note">
            <Link to="/progression/activite">Voir les activités importées</Link>
          </p>
        </>
      )}
    </>
  );
}
